const AUDIO_EXTENSION = /\.(?:aac|flac|m4a|mp3|ogg|oga|opus|wav)(?:$|[?#])/i;
const VIDEO_EXTENSION = /\.(?:m4v|mp4|mov|ogv|webm)(?:$|[?#])/i;
const CREATOR_PLAYBACK_SURFACE = 'universal';

const text = (value) => String(value || '').trim();

const firstHttpUrl = (...values) => values
  .flat(Infinity)
  .map(text)
  .find((value) => /^https?:\/\//i.test(value)) || '';

const extractYouTubeId = (value) => {
  const source = text(value);
  if (!source) return '';
  if (/^[A-Za-z0-9_-]{6,15}$/.test(source) && !source.includes('/')) return source;

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') return parsed.pathname.split('/').filter(Boolean)[0] || '';
    if (!['youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(host)) return '';
    if (parsed.searchParams.get('v')) return parsed.searchParams.get('v');
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (['embed', 'shorts', 'live'].includes(parts[0])) return parts[1] || '';
  } catch {
    return '';
  }
  return '';
};

const spotifyEmbedUrl = (value, fallbackId = '', fallbackType = 'album') => {
  const source = text(value);
  if (source) {
    try {
      const parsed = new URL(source);
      if (parsed.hostname.replace(/^www\./, '').toLowerCase() === 'open.spotify.com') {
        const parts = parsed.pathname.split('/').filter(Boolean);
        const offset = parts[0] === 'embed' ? 1 : 0;
        const type = parts[offset];
        const id = parts[offset + 1];
        if (type && id) return `https://open.spotify.com/embed/${type}/${id}`;
      }
    } catch {
      // Fall through to a stored Spotify id.
    }
  }
  return fallbackId ? `https://open.spotify.com/embed/${fallbackType}/${fallbackId}` : '';
};

const providerEmbed = (value) => {
  const source = text(value);
  if (!source) return null;

  const spotify = spotifyEmbedUrl(source);
  if (spotify) return { provider: 'spotify', embedUrl: spotify };

  try {
    const parsed = new URL(source);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'soundcloud.com' || host.endsWith('.soundcloud.com')) {
      return {
        provider: 'soundcloud',
        embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(source)}&auto_play=true&hide_related=false&show_comments=false&show_user=true&show_reposts=false&visual=false`,
      };
    }
    if (host === 'mixcloud.com' || host.endsWith('.mixcloud.com')) {
      const feed = parsed.pathname.endsWith('/') ? parsed.pathname : `${parsed.pathname}/`;
      return {
        provider: 'mixcloud',
        embedUrl: `https://www.mixcloud.com/widget/iframe/?hide_cover=1&autoplay=1&feed=${encodeURIComponent(feed)}`,
      };
    }
    if (host === 'vimeo.com' || host.endsWith('.vimeo.com')) {
      const videoId = parsed.pathname.split('/').filter(Boolean).find((part) => /^\d+$/.test(part));
      if (videoId) {
        return { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1` };
      }
    }
  } catch {
    return null;
  }
  return null;
};

const socialVideoUrl = (item = {}) => {
  const media = Array.isArray(item.media) ? item.media : [];
  const variants = media
    .flatMap((entry) => (Array.isArray(entry?.variants) ? entry.variants : []))
    .filter((variant) => /^video\//i.test(text(variant?.content_type)) && /^https?:\/\//i.test(text(variant?.url)))
    .sort((left, right) => Number(right.bitrate || 0) - Number(left.bitrate || 0));
  const declaredVideo = /video|reel/i.test(text(item.media_type || item.type));
  const videoMedia = media.filter((entry) => /video|animated_gif/i.test(text(entry?.type || entry?.media_type)));
  return firstHttpUrl(
    item.video_url,
    declaredVideo ? item.media_url : '',
    variants[0]?.url,
    videoMedia.map((entry) => entry?.media_url || entry?.url),
  );
};

const creatorMediaId = (item, type, source) => {
  if (text(item.track_id)) return text(item.track_id);
  const stored = text(
    item.youtube_id
    || item.video_id
    || item.spotify_id
    || item.release_id
    || item.id
    || item.hearthis_id,
  );
  if (type === 'youtube') return text(item.youtube_id || item.video_id) || stored;
  if (type === 'mixtape' && stored) return stored;
  return stored ? `${type}:${stored}` : `${type}:${source}`;
};

const normalizeCreatorMedia = (rawItem, options = {}) => {
  const item = typeof rawItem === 'string' ? { url: rawItem } : (rawItem || {});
  const kind = text(options.kind || item.kind || item.track_type || item.type).toLowerCase();
  const title = text(item.title || item.name || options.title || 'Creator media');
  const artist = text(
    item.artist_name
    || item.artist
    || item.dj
    || item.channel_title
    || options.artist,
  );
  const cover = firstHttpUrl(
    item.cover_image,
    item.cover_url,
    item.cover,
    item.thumbnail_url,
    item.thumbnail,
    item.image,
    options.cover,
  ) || text(item.cover_image || item.cover_url || item.cover || item.thumbnail_url || item.thumbnail || item.image || options.cover);
  const linkedUrl = firstHttpUrl(
    item.url,
    item.link,
    item.externalUrl,
    item.spotify_url,
    item.hearthis_url,
    item.embed_url,
  );
  const youtubeId = text(item.youtube_id || item.video_id)
    || [item.url, item.link, item.externalUrl, item.embed_url].map(extractYouTubeId).find(Boolean)
    || '';
  const creatorMeta = {
    creator_id: text(item.creator_id || options.creatorId),
    creator_slug: text(item.creator_slug || options.creatorSlug),
    playback_surface: text(item.playback_surface || options.playbackSurface),
  };

  if (youtubeId) {
    const externalUrl = linkedUrl || `https://www.youtube.com/watch?v=${youtubeId}`;
    return {
      ...creatorMeta,
      track_id: creatorMediaId({ ...item, youtube_id: youtubeId }, 'youtube', externalUrl),
      track_type: 'youtube',
      youtube_id: youtubeId,
      title,
      artist: artist || 'YouTube',
      cover,
      url: externalUrl,
      externalUrl,
      provider: 'youtube',
      video_type: item.video_type || options.videoType || 'creator',
    };
  }

  const directUrl = firstHttpUrl(
    item.resolved_audio_url,
    item.audio_url,
    item.stream_url,
    item.radio_stream_url,
    socialVideoUrl(item),
  );
  const declaredVideo = /video|reel/i.test(text(item.media_type || item.type || kind));
  if (directUrl) {
    const type = declaredVideo || VIDEO_EXTENSION.test(directUrl)
      ? 'video'
      : (kind === 'radio' || kind === 'radio_station' ? 'radio' : (kind === 'mixtape' ? 'mixtape' : 'audio'));
    return {
      ...item,
      ...creatorMeta,
      track_id: creatorMediaId(item, type, directUrl),
      track_type: type,
      title,
      artist,
      cover,
      url: directUrl,
      stream_url: type === 'video' ? '' : directUrl,
      video_url: type === 'video' ? directUrl : '',
      externalUrl: linkedUrl || directUrl,
      provider: type === 'radio' ? 'live-stream' : 'direct',
    };
  }

  const hearthisUrl = firstHttpUrl(item.hearthis_url) || (/hearthis\.at/i.test(linkedUrl) ? linkedUrl : '');
  if (hearthisUrl || (kind === 'mixtape' && (item.id || item.track_id))) {
    return {
      ...item,
      ...creatorMeta,
      track_id: creatorMediaId(item, 'mixtape', hearthisUrl),
      track_type: 'mixtape',
      title,
      artist,
      cover,
      hearthis_url: hearthisUrl,
      url: hearthisUrl,
      externalUrl: hearthisUrl,
      synced_from_hearthis: Boolean(item.synced_from_hearthis || hearthisUrl),
      provider: 'hearthis',
    };
  }

  const storedEmbed = firstHttpUrl(item.embed_url);
  const embed = providerEmbed(storedEmbed || linkedUrl);
  const spotifyId = text(item.spotify_id);
  const spotifyType = text(item.spotify_type || options.spotifyType || (kind === 'spotify-profile' ? 'artist' : 'album'));
  const embedUrl = embed?.embedUrl || spotifyEmbedUrl('', spotifyId, spotifyType);
  if (embedUrl) {
    const provider = embed?.provider || 'spotify';
    const externalUrl = firstHttpUrl(item.spotify_url, item.url, item.link, item.externalUrl) || linkedUrl;
    return {
      ...item,
      ...creatorMeta,
      track_id: creatorMediaId(item, 'embed', embedUrl),
      track_type: 'embed',
      title,
      artist,
      cover,
      embed_url: embedUrl,
      url: externalUrl,
      externalUrl,
      provider,
    };
  }

  if (linkedUrl && AUDIO_EXTENSION.test(linkedUrl)) {
    return {
      ...creatorMeta,
      track_id: creatorMediaId(item, 'audio', linkedUrl),
      track_type: 'audio',
      title,
      artist,
      cover,
      url: linkedUrl,
      stream_url: linkedUrl,
      externalUrl: linkedUrl,
      provider: 'direct',
    };
  }
  if (linkedUrl && VIDEO_EXTENSION.test(linkedUrl)) {
    return {
      ...creatorMeta,
      track_id: creatorMediaId(item, 'video', linkedUrl),
      track_type: 'video',
      title,
      artist,
      cover,
      url: linkedUrl,
      video_url: linkedUrl,
      externalUrl: linkedUrl,
      provider: 'direct',
    };
  }

  return null;
};

const uniqueCreatorMedia = (items = []) => {
  const seen = new Set();
  return items.filter(Boolean).filter((item) => {
    const key = `${item.track_type}:${item.youtube_id || item.embed_url || item.url || item.track_id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const collectCreatorMedia = ({ creator = {}, content = {}, socialContent = {} } = {}) => {
  const artist = creator.display_name || creator.stage_name || '';
  const cover = creator.profile_image || '';
  const candidates = [];
  const creatorId = creator.canonical_creator_id || creator.creator_id || '';
  const creatorSlug = creator.slug || '';
  const add = (item, options) => candidates.push(normalizeCreatorMedia(item, {
    artist,
    cover,
    creatorId,
    creatorSlug,
    playbackSurface: CREATOR_PLAYBACK_SURFACE,
    ...options,
  }));

  if (creator.featured_video_url) {
    add({ url: creator.featured_video_url }, { kind: 'featured-video', title: `${artist} featured video` });
  }
  (creator.signature_tracks || []).forEach((item) => add(item, { kind: 'signature' }));
  (content.releases || []).forEach((item) => add(item, { kind: 'release', spotifyType: 'album' }));
  (content.mixtapes || []).forEach((item) => add(item, { kind: 'mixtape' }));
  (socialContent.spotify?.releases || []).forEach((item) => add(item, { kind: 'release', spotifyType: 'album' }));
  (socialContent.youtube?.videos || []).forEach((item) => add(item, { kind: 'youtube' }));
  (socialContent.instagram?.posts || []).forEach((item) => add(item, { kind: 'instagram-video' }));
  (socialContent.twitter?.posts || []).forEach((item) => add(item, { kind: 'twitter-video' }));
  if (creator.radio_stream_url) {
    add(
      { id: creator.creator_id || creator.slug, radio_stream_url: creator.radio_stream_url },
      { kind: 'radio', title: `${artist} live`, artist: `${artist} Radio` },
    );
  }
  return uniqueCreatorMedia(candidates);
};

export {
  CREATOR_PLAYBACK_SURFACE,
  collectCreatorMedia,
  extractYouTubeId,
  normalizeCreatorMedia,
  providerEmbed,
  spotifyEmbedUrl,
  uniqueCreatorMedia,
};
